import { Injectable, inject } from '@angular/core';
import { environment } from 'src/environments/environment';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError, lastValueFrom } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AlertService } from './alert.service';
import { TranslocoService } from '@jsverse/transloco';

@Injectable({
  providedIn: 'root',
})
export class HttpService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private alertService = inject(AlertService);
  private translocoService = inject(TranslocoService);

  private headers: HttpHeaders = new HttpHeaders();

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {}

  private setHeaders(captcha?: string, responseType?: string): HttpHeaders {
    // Get the language from localStorage
    const currentLang = localStorage.getItem('lang') || 'fr';

    let headers = new HttpHeaders()
      .set('Accept', '*/*')
      .set('Content-Type', 'application/json')
      .set('Accept-Language', currentLang);

    if (captcha) {
      headers = headers.set('Captcha-Token', captcha);
    }

    const token = localStorage.getItem('accessToken');
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }

    return headers;
  }

  async post<T>(
    route: string,
    data: any,
    captcha: string = '',
    responseType: any = 'json',
    options?: { skipErrorAlert?: boolean },
  ): Promise<T> {
    const headers = this.setHeaders(captcha);
    return lastValueFrom(
      this.http
        .post<T>(`${environment.apiUrl}${route}`, data, {
          headers,
          responseType: responseType as any,
        })
        .pipe(catchError((error) => (options?.skipErrorAlert ? throwError(() => error) : this.handleHttpError(error)))),
    );
  }

  async get<T>(
    route: string,
    params: HttpParams | { [key: string]: string | number | boolean | readonly (string | number | boolean)[] } = {},
    captcha: string = '',
    options?: { skipErrorAlert?: boolean },
  ): Promise<T> {
    const headers = this.setHeaders(captcha);
    const httpParams = params instanceof HttpParams ? params : new HttpParams({ fromObject: params });

    return lastValueFrom(
      this.http
        .get<T>(`${environment.apiUrl}${route}`, {
          params: httpParams,
          headers,
        })
        .pipe(catchError((error) => (options?.skipErrorAlert ? throwError(() => error) : this.handleHttpError(error)))),
    );
  }

  async delete<T>(route: string): Promise<T> {
    const headers = this.setHeaders();
    return lastValueFrom(
      this.http
        .delete<T>(`${environment.apiUrl}${route}`, {
          headers,
        })
        .pipe(catchError((error) => this.handleHttpError(error))),
    );
  }

  async put<T>(route: string, data: any): Promise<T> {
    const headers = this.setHeaders();
    const url = `${environment.apiUrl}${route}`;

    try {
      return await lastValueFrom(
        this.http.put<T>(url, data, { headers }).pipe(catchError((error) => this.handleHttpError(error))),
      );
    } catch (error) {
      console.error('PUT request failed:', error);
      throw error;
    }
  }

  async patch<T>(route: string, data: any): Promise<T> {
    const headers = this.setHeaders();
    const url = `${environment.apiUrl}${route}`;

    try {
      return await lastValueFrom(
        this.http.patch<T>(url, data, { headers }).pipe(catchError((error) => this.handleHttpError(error))),
      );
    } catch (error) {
      console.error('PUT request failed:', error);
      throw error;
    }
  }

  async getFile(route: string, params: HttpParams | { [key: string]: string } = {}): Promise<ArrayBuffer> {
    const headers = this.setHeaders();
    const httpParams = params instanceof HttpParams ? params : new HttpParams({ fromObject: params });

    return lastValueFrom(
      this.http
        .get(`${environment.apiUrl}${route}`, {
          params: httpParams,
          responseType: 'arraybuffer',
          headers,
        })
        .pipe(catchError((error) => this.handleHttpError(error))),
    );
  }

  async downloadFile(route: string, params: HttpParams | { [key: string]: string } = {}): Promise<Blob> {
    const headers = this.setHeaders();
    const httpParams = params instanceof HttpParams ? params : new HttpParams({ fromObject: params });

    return lastValueFrom(
      this.http
        .get(`${environment.apiUrl}${route}`, {
          params: httpParams,
          responseType: 'blob',
          headers,
        })
        .pipe(catchError((error) => this.handleHttpError(error))),
    );
  }

  /**
   * A file and the name the server gave it (Content-Disposition), for saving it under a name
   * that says what it is. Falls back to `fallbackName` when the header is absent.
   */
  async downloadNamedFile(route: string, fallbackName: string): Promise<{ blob: Blob; fileName: string }> {
    const response = await lastValueFrom(
      this.http
        .get(`${environment.apiUrl}${route}`, {
          responseType: 'blob',
          observe: 'response',
          headers: this.setHeaders(),
        })
        .pipe(catchError((error) => this.handleHttpError(error))),
    );
    return {
      blob: response.body ?? new Blob(),
      fileName: fileNameFromDisposition(response.headers.get('Content-Disposition')) ?? fallbackName,
    };
  }

  private handleHttpError(error: any): Observable<never> {
    // console.error('HTTP Error occurred:', error);

    if (error instanceof HttpErrorResponse) {
      const errorMessage =
        error.error?.message || error.error || this.translocoService.translate('HTTP.UNEXPECTED_ERROR');
      console.error('Error details:', {
        status: error.status,
        statusText: error.statusText,
        url: error.url,
        message: errorMessage,
        error: error.error,
      });

      /*
       * The server refuses paid features and the headcount cap with codes of
       * its own. Neither is the user's fault, so neither interrupts them: the
       * menu and `PlanGuard` already keep paid features out of reach, so a 403
       * here means a stale link and passes in silence, and the cap - which the
       * user does hit mid-task - gets a toast rather than a modal.
       */
      const planCode = error.error?.code;
      if (error.status === 403 && planCode === 'PLAN_UPGRADE_REQUIRED') {
        return throwError(() => error);
      }
      if (error.status === 409 && planCode === 'EMPLOYEE_LIMIT_REACHED') {
        this.alertService.error(
          this.translocoService.translate('PLAN.LIMIT_REACHED_HINT'),
          this.translocoService.translate('PLAN.EMPLOYEE_LIMIT_MESSAGE', {
            limit: error.error?.limit,
            current: error.error?.current,
          }),
        );
        return throwError(() => error);
      }

      switch (error.status) {
        case 0:
          this.alertService.showAlert(
            'error',
            this.translocoService.translate('HTTP.SERVICE_ERROR_TITLE'),
            this.translocoService.translate('HTTP.SERVICE_ERROR_TEXT'),
          );
          break;
        case 401:
          this.alertService.showAlert(
            'error',
            this.translocoService.translate('HTTP.UNAUTHORIZED_TITLE'),
            errorMessage,
          );
          this.logout();
          break;
        case 500:
          this.alertService.showAlert(
            'error',
            this.translocoService.translate('HTTP.SERVER_ERROR_TITLE'),
            this.translocoService.translate('HTTP.SERVER_ERROR_TEXT'),
          );
          break;
        default:
          this.alertService.showAlert(
            'error',
            this.translocoService.translate('HTTP.ERROR_TITLE', { status: error.status }),
            errorMessage,
          );
          break;
      }
    } else {
      console.error('Non-HTTP error:', error);
      this.alertService.showAlert(
        'error',
        this.translocoService.translate('HTTP.UNEXPECTED_ERROR_TITLE'),
        this.translocoService.translate('HTTP.UNEXPECTED_ERROR_TEXT'),
      );
    }

    return throwError(() => error);
  }

  async logout(): Promise<void> {
    // Only the session goes: clearing all storage also lost the language, the app version and the
    // remembered sign-in. AuthService cannot be injected here (it depends on this service).
    for (const storage of [localStorage, sessionStorage]) {
      storage.removeItem('tokenData');
      storage.removeItem('user');
    }
    await this.router.navigate(['auth/sign-in']);
  }
}

/** `attachment; filename="report.csv"` (or the RFC 5987 `filename*=UTF-8''...` form) → `report.csv`. */
export function fileNameFromDisposition(header: string | null): string | null {
  if (!header) return null;
  const extended = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(header);
  if (extended) {
    try {
      return decodeURIComponent(extended[1].trim().replace(/^"|"$/g, ''));
    } catch {
      /* fall through to the plain form */
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain ? plain[1].trim() : null;
}
